import React, {
	forwardRef,
	Ref,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import { loadScripts } from "@/utils/script";
import {
	RequestPayParams,
	RequestPayResponseCallback,
} from "@/components/IamportLoader/types";
import { getSubscribeUniqueKey } from "@/views/Price/api";
import axios from "axios";
import { useSnackbar } from "notistack";

interface RequestPayParam {
	params: Omit<RequestPayParams, "customer_uid">;
	planId: number;
	callback?: RequestPayResponseCallback;
}

export interface IamportLoaderRefType {
	requestPay: (param: RequestPayParam) => void;
}

interface IamportLoaderProps {
	children: React.ReactElement;
}

const IamportLoader = forwardRef(
	({ children }: IamportLoaderProps, ref: Ref<IamportLoaderRefType>) => {
		const [isLoaded, setIsLoaded] = useState<boolean>(false);
		const { enqueueSnackbar } = useSnackbar();
		const [IMP, jQuery] = useMemo(
			() => [window.IMP, window.jQuery],
			[window.IMP, window.jQuery],
		);

		async function load() {
			await loadScripts({
				jquery: "https://code.jquery.com/jquery-1.12.4.min.js",
				iamport: "https://cdn.iamport.kr/js/iamport.payment-1.2.0.js",
			});

			setIsLoaded(true);
		}

		async function requestPay({ params, callback, planId }: RequestPayParam) {
			if (!window.IMP || !window.jQuery) {
				return;
			}

			let customer_uid;
			try {
				customer_uid = await getSubscribeUniqueKey(planId);

				if (!customer_uid) {
					throw new Error("");
				}

				window.IMP.request_pay(
					{
						...params,
						customer_uid,
					},
					callback,
				);
			} catch (e) {
				if (axios.isAxiosError(e)) {
					enqueueSnackbar("정기결제 Unique Key 발급 오류.", {
						variant: "error",
					});
				}
			}
		}

		useImperativeHandle(ref, () => ({
			requestPay,
		}));

		useEffect(() => {
			load();
		}, []);

		useEffect(() => {
			if (!IMP || !jQuery) {
				return;
			}
			const initCode =
				process.env.REACT_APP_MODE === "development"
					? process.env.REACT_APP_DEV_IAMPORT_CODE
					: process.env.REACT_APP_IAMPORT_CODE;
			if (initCode) {
				IMP.init(initCode);
			}
		}, [IMP, jQuery]);

		return <>{isLoaded ? children : null}</>;
	},
);

export default IamportLoader;
